import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ProductViewBase, EntitlementViewBase } from '@amzn/coral_com-amazon-sssapinterface-model-structures-credits';
import { useBundle } from '@amzn/react-arb-tools';
import { Breadcrumbs, useLogger, useApi, Loader, LOADER_SIZE } from '@amzn/sss-website-theme';

import { useSssApiCreditsClient } from 'src/clients';
import { ErrorFallback } from 'src/components/common/error/ErrorFallback';
import {
  StaticWorkflowTracker,
  StaticWorkflowStep,
} from 'src/components/pages/checkout/static-workflow-tracker/StaticWorkflowTracker';
import { COMMON_BUNDLE_NAME, CHECKOUT_BUNDLE_NAME } from 'src/constants';
import {
  CARBON_CREDIT_SERVICE_LABEL,
  CHECKOUT_LABEL,
  HOMEPAGE_LABEL,
  HOMEPAGE_URL,
} from 'src/constants/pages/page-constants';
import { EAC_ENTITLEMENT_STATE, ROUTES } from 'src/enums';
import { t } from 'src/helpers/translations/translation-helpers';

import styles from './CheckoutPage.module.scss';
import { CheckoutStep4, CheckoutStep1, CheckoutStep2, CheckoutStep3 } from './checkout-steps';

interface CheckoutData {
  product: ProductViewBase;
  entitlement?: EntitlementViewBase;
}

const REQUEST_CREDITS_STEP_INDEX = 0;
const SIGN_AGREEMENTS_STEP_INDEX = 1;
const COMPLETE_PURCHASE_STEP_INDEX = 2;
const SUBMIT_PAYMENT_STEP_INDEX = 3;

const CHECKOUT_STEP_INDICES_BY_ENTITLEMENT_STATE: Partial<Record<EAC_ENTITLEMENT_STATE, number>> = {
  [EAC_ENTITLEMENT_STATE.PENDING_REVIEW]: SIGN_AGREEMENTS_STEP_INDEX,
  [EAC_ENTITLEMENT_STATE.PLANNED]: SIGN_AGREEMENTS_STEP_INDEX,
  [EAC_ENTITLEMENT_STATE.LOCKED]: COMPLETE_PURCHASE_STEP_INDEX,
  [EAC_ENTITLEMENT_STATE.PURCHASING]: SUBMIT_PAYMENT_STEP_INDEX,
  [EAC_ENTITLEMENT_STATE.FULFILLED]: SUBMIT_PAYMENT_STEP_INDEX,
};

export const CheckoutPage = () => {
  const [commonBundle] = useBundle(COMMON_BUNDLE_NAME);
  const [checkoutBundle] = useBundle(CHECKOUT_BUNDLE_NAME);
  const { asin, entitlementId } = useParams<{ asin: string; entitlementId?: string }>();
  const [hasFetchAttempted, setHasFetchAttempted] = useState(false);

  const creditsClient = useSssApiCreditsClient();
  const logger = useLogger();

  const customErrorHandler = (err: Error) => {
    // istanbul ignore next
    logger.logError('CheckoutPage', 'Error fetching data', err, { asin, entitlementId });
  };

  const { isLoading, error, response, callApi } = useApi<CheckoutData>(customErrorHandler);

  useEffect(() => {
    if (!asin) {
      setHasFetchAttempted(true); // Mark as attempted even if no asin
      return;
    }

    // istanbul ignore next
    const fetchData = async () => {
      const productResponse = await creditsClient.getEacProduct(encodeURIComponent(asin));
      const data: CheckoutData = { product: productResponse.product };

      if (entitlementId) {
        const entitlementResponse = await creditsClient.getEacEntitlement(entitlementId);
        data.entitlement = entitlementResponse.entitlement;
      }

      return data;
    };

    setHasFetchAttempted(true);
    callApi(fetchData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asin, entitlementId, creditsClient]);

  const getActiveStep = (): number =>
    response?.entitlement
      ? (CHECKOUT_STEP_INDICES_BY_ENTITLEMENT_STATE[response.entitlement.entitlementState as EAC_ENTITLEMENT_STATE] ??
        REQUEST_CREDITS_STEP_INDEX)
      : REQUEST_CREDITS_STEP_INDEX;

  const workflowSteps: StaticWorkflowStep[] = useMemo(
    () => [
      {
        stepLabel: t(checkoutBundle, 'checkout_stepper_1_label'),
        stepComponent: <CheckoutStep1 product={response?.product} />,
      },
      {
        stepLabel: t(checkoutBundle, 'checkout_stepper_2_label'),
        stepComponent: <CheckoutStep2 product={response?.product} entitlement={response?.entitlement} />,
      },
      {
        stepLabel: t(checkoutBundle, 'checkout_stepper_3_label'),
        stepComponent: <CheckoutStep3 product={response?.product} entitlement={response?.entitlement} />,
      },
      {
        stepLabel: t(checkoutBundle, 'checkout_stepper_4_label'),
        stepComponent: <CheckoutStep4 product={response?.product} entitlement={response?.entitlement} />,
      },
    ],
    [checkoutBundle, response?.product, response?.entitlement]
  );

  if (isLoading || !hasFetchAttempted) {
    return (
      <section className={classNames(styles.checkoutPage, styles.loader)}>
        <Loader label={t(commonBundle, 'loading_aria_label')} size={LOADER_SIZE.LARGE} />
      </section>
    );
  }

  if (!asin || error || !response?.product) {
    return <ErrorFallback />;
  }

  const parentBreadcrumb = response?.product.productTitle ?? t(commonBundle, CARBON_CREDIT_SERVICE_LABEL);

  return (
    <section className={styles.checkoutPage}>
      <div className={styles.checkoutPageContent}>
        <Breadcrumbs
          homepageLink={{ label: t(commonBundle, HOMEPAGE_LABEL), url: HOMEPAGE_URL }}
          parentLink={{
            label: parentBreadcrumb,
            url: ROUTES.PRODUCT_PAGE.replace(':asin', response?.product.asin),
          }}
          currentPage={t(commonBundle, CHECKOUT_LABEL)}
          className={styles.breadcrumbs}
        />
        <StaticWorkflowTracker
          workflowTitle={t(commonBundle, CHECKOUT_LABEL)}
          workflowSteps={workflowSteps}
          activeStep={getActiveStep()}
        />
      </div>
    </section>
  );
};
